import { faPlus, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { Button } from '../../ui/buttons/Button';

export type ExpandingCustomSpaceInputProps = {
  isExpanded: boolean;
  onExpand: () => void;
  onAdd: (spaceName: string) => void;
  onCancel: () => void;
  existingNames: string[];
  /** Bounds of the container to stay within (desktop only) */
  expandBounds?: { top: number; left: number; width: number; height: number };
  /** Visual style for the trigger and animation */
  variant?: 'card' | 'pill';
  triggerLabel?: string;
  triggerAriaLabel?: string;
  addButtonLabel?: string;
  duplicateMessage?: string;
  placeholder?: string;
  /** Higher z-index for nested expansions (when expanding inside another expanded card) */
  nestedExpansion?: boolean;
};

export function ExpandingCustomSpaceInput({
  isExpanded,
  onExpand,
  onAdd,
  onCancel,
  existingNames,
  expandBounds,
  variant = 'card',
  triggerLabel,
  triggerAriaLabel,
  addButtonLabel = 'Add Space',
  duplicateMessage = 'Already exists',
  placeholder = 'Space name...',
  nestedExpansion = false,
}: ExpandingCustomSpaceInputProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [originRect, setOriginRect] = useState<DOMRect | null>(null);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { isTablet } = useBreakpoint();

  const effectiveTriggerLabel =
    triggerLabel ?? (variant === 'card' ? 'Custom' : 'Space');
  const effectiveTriggerAriaLabel =
    triggerAriaLabel ??
    (variant === 'card' ? 'Add custom space' : 'Add a new space');

  // Capture button rect when expanding
  const handleExpand = (event: React.MouseEvent) => {
    // Prevent event from bubbling to parent elements (important for nested expansions)
    event.stopPropagation();

    if (buttonRef.current) {
      setOriginRect(buttonRef.current.getBoundingClientRect());
    }
    onExpand();
  };

  // Capture origin rect when isExpanded becomes true (in case component remounts)
  // NOTE: This effect is critical for nested expansions where parent re-renders can cause
  // the component to remount with isExpanded=true but originRect=null
  useEffect(() => {
    if (isExpanded && !originRect && buttonRef.current) {
      setOriginRect(buttonRef.current.getBoundingClientRect());
    }
  }, [isExpanded, originRect]);

  // Focus input when expanded - with delay to allow animation to complete
  useEffect(() => {
    if (isExpanded) {
      const delay = isTablet ? 300 : 100;
      const timer = setTimeout(() => inputRef.current?.focus(), delay);
      return () => clearTimeout(timer);
    }
  }, [isExpanded, isTablet]);

  const handleAdd = () => {
    const trimmed = value.trim();
    const isDuplicate = existingNames.some(
      (name) => name.toLowerCase() === trimmed.toLowerCase()
    );
    if (trimmed && !isDuplicate) {
      onAdd(trimmed);
      setValue('');
    }
  };

  const handleCancel = () => {
    setValue('');
    onCancel();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAdd();
    }
    if (event.key === 'Escape') {
      handleCancel();
    }
  };

  const isDuplicate = existingNames.some(
    (name) => name.toLowerCase() === value.trim().toLowerCase()
  );
  const isValid = value.trim().length > 0 && !isDuplicate;

  // Z-index values: higher for nested expansions to appear above parent expanded cards
  const backdropZIndex = nestedExpansion ? 'z-[110]' : 'z-[100]';
  const cardZIndex = nestedExpansion ? 'z-[111]' : 'z-[101]';

  // Spring animation config
  const springConfig = {
    type: 'spring' as const,
    stiffness: 400,
    damping: 35,
    mass: 1,
  };

  // Calculate expanded size - just enough to fit content
  const EXPANDED_WIDTH = 220;
  const EXPANDED_HEIGHT = 150;
  const PADDING = 8;

  // Mobile: Bottom sheet height
  const MOBILE_SHEET_HEIGHT = 220;

  // Calculate expanded position, clamped to container bounds (desktop only)
  const getExpandedPosition = () => {
    if (!originRect) return { top: 0, left: 0 };

    // Use expandBounds if provided, otherwise fallback to viewport
    const bounds = expandBounds ?? {
      top: 0,
      left: 0,
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // Center on button
    let top = originRect.top - (EXPANDED_HEIGHT - originRect.height) / 2;
    let left = originRect.left - (EXPANDED_WIDTH - originRect.width) / 2;

    // Clamp to container bounds
    const minTop = bounds.top + PADDING;
    const maxTop = bounds.top + bounds.height - EXPANDED_HEIGHT - PADDING;
    const minLeft = bounds.left + PADDING;
    const maxLeft = bounds.left + bounds.width - EXPANDED_WIDTH - PADDING;

    top = Math.max(minTop, Math.min(top, maxTop));
    left = Math.max(minLeft, Math.min(left, maxLeft));

    return { top, left };
  };

  const expandedPos = getExpandedPosition();

  // Shared form content
  const FormContent = (
    <>
      {/* Header */}
      <div className='mb-3 flex items-center justify-between'>
        <span className='text-base font-semibold text-grey-70 sm:text-sm'>
          Add Custom Space
        </span>
        <button
          type='button'
          onClick={handleCancel}
          className='flex h-8 w-8 items-center justify-center rounded-full text-grey-40 hover:bg-grey-10 hover:text-grey-70 sm:h-6 sm:w-6'
          aria-label='Cancel'
        >
          <FontAwesomeIcon icon={faTimes} className='h-4 w-4 sm:h-3 sm:w-3' />
        </button>
      </div>

      {/* Form */}
      <input
        ref={inputRef}
        type='text'
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label='Custom space name'
        className='mb-2 w-full rounded-lg border border-grey-20 px-4 py-3 text-base focus:border-brand focus:outline-none sm:rounded sm:px-3 sm:py-2 sm:text-sm'
        maxLength={50}
        // Prevent iOS zoom by ensuring touch-action and font-size are appropriate
        style={{ fontSize: '16px' }}
      />
      {isDuplicate && (
        <span className='mb-2 text-xs text-red-500'>{duplicateMessage}</span>
      )}

      {/* Add button */}
      <Button
        type='button'
        onClick={handleAdd}
        disabled={!isValid}
        color='primary'
        className='mt-auto w-full'
      >
        {addButtonLabel}
      </Button>
    </>
  );

  const renderTrigger = () => {
    if (variant === 'pill') {
      return (
        <button
          ref={buttonRef}
          type='button'
          onClick={handleExpand}
          className='hover:bg-brand/5 rounded-full border-2 border-dashed border-grey-30 px-3 py-1.5 text-sm font-medium text-grey-50 transition-all hover:border-brand hover:text-brand'
          tabIndex={isExpanded ? -1 : 0}
          aria-label={effectiveTriggerAriaLabel}
        >
          <FontAwesomeIcon icon={faPlus} className='mr-1 h-3 w-3' />
          {effectiveTriggerLabel}
        </button>
      );
    }

    return (
      <button
        ref={buttonRef}
        type='button'
        onClick={handleExpand}
        className='hover:bg-brand/5 flex min-h-[100px] w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-grey-30 p-3 transition-all hover:border-brand'
        tabIndex={isExpanded ? -1 : 0}
        aria-label={effectiveTriggerAriaLabel}
      >
        <div className='mb-2 flex h-8 w-8 items-center justify-center rounded-full text-grey-40'>
          <FontAwesomeIcon icon={faPlus} className='h-5 w-5' />
        </div>
        <span className='text-sm font-medium text-grey-50'>
          {effectiveTriggerLabel}
        </span>
      </button>
    );
  };

  const renderMobileSheet = () => {
    if (variant === 'pill') {
      return (
        <motion.div
          className={`fixed bottom-0 left-0 right-0 ${cardZIndex} overflow-hidden rounded-t-2xl bg-white p-4 shadow-2xl`}
          initial={{ y: MOBILE_SHEET_HEIGHT }}
          animate={{ y: 0 }}
          exit={{ y: MOBILE_SHEET_HEIGHT }}
          transition={{ type: 'spring', stiffness: 400, damping: 35 }}
        >
          {FormContent}
        </motion.div>
      );
    }

    return (
      <motion.div
        className={`fixed ${cardZIndex} overflow-hidden bg-white shadow-2xl`}
        initial={{
          top: originRect?.top ?? 0,
          left: originRect?.left ?? 0,
          width: originRect?.width ?? 0,
          height: originRect?.height ?? 0,
          borderRadius: 12,
        }}
        animate={{
          top: window.innerHeight - MOBILE_SHEET_HEIGHT,
          left: 0,
          width: window.innerWidth,
          height: MOBILE_SHEET_HEIGHT,
          borderRadius: '16px 16px 0 0',
        }}
        exit={{
          top: originRect?.top ?? 0,
          left: originRect?.left ?? 0,
          width: originRect?.width ?? 0,
          height: originRect?.height ?? 0,
          borderRadius: 12,
          opacity: 0,
        }}
        transition={{
          ...springConfig,
          opacity: { duration: 0.15 },
        }}
        // Prevent drag-to-dismiss interfering with input
        style={{ touchAction: 'none' }}
      >
        <motion.div
          className='flex h-full flex-col'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ delay: 0.05, duration: 0.15 }}
        >
          {/* Handle indicator */}
          <div className='flex justify-center pb-2 pt-3'>
            <div className='h-1 w-10 rounded-full bg-grey-30' />
          </div>

          {/* Form content with safe area padding */}
          <div className='flex flex-1 flex-col px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))]'>
            {FormContent}
          </div>
        </motion.div>
      </motion.div>
    );
  };

  const renderDesktopCard = () => {
    if (variant === 'pill') {
      return (
        <motion.div
          className={`fixed ${cardZIndex} flex w-[220px] flex-col overflow-hidden rounded-2xl bg-white p-4 shadow-2xl`}
          initial={{
            top: originRect?.top ?? 0,
            left: originRect?.left ?? 0,
            width: originRect?.width ?? 0,
            height: originRect?.height ?? 0,
            opacity: 1,
          }}
          animate={{
            top: expandedPos.top,
            left: expandedPos.left,
            width: EXPANDED_WIDTH,
            height: EXPANDED_HEIGHT,
            opacity: 1,
          }}
          exit={{
            top: originRect?.top ?? 0,
            left: originRect?.left ?? 0,
            width: originRect?.width ?? 0,
            height: originRect?.height ?? 0,
            opacity: 0,
          }}
          transition={{
            ...springConfig,
            opacity: { duration: 0.2, ease: 'easeOut' },
          }}
        >
          {FormContent}
        </motion.div>
      );
    }

    return (
      <motion.div
        className={`fixed ${cardZIndex} overflow-hidden rounded-xl bg-white shadow-2xl`}
        initial={{
          top: originRect?.top ?? 0,
          left: originRect?.left ?? 0,
          width: originRect?.width ?? 0,
          height: originRect?.height ?? 0,
          opacity: 1,
        }}
        animate={{
          top: expandedPos.top,
          left: expandedPos.left,
          width: EXPANDED_WIDTH,
          height: EXPANDED_HEIGHT,
          opacity: 1,
        }}
        exit={{
          top: originRect?.top ?? 0,
          left: originRect?.left ?? 0,
          width: originRect?.width ?? 0,
          height: originRect?.height ?? 0,
          opacity: 0,
        }}
        transition={{
          ...springConfig,
          opacity: { duration: 0.15 },
        }}
      >
        <motion.div
          className='flex h-full flex-col p-4'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ delay: 0.05, duration: 0.15 }}
        >
          {FormContent}
        </motion.div>
      </motion.div>
    );
  };

  const shouldRenderPortal = isExpanded && !!originRect;

  return (
    <>
      {renderTrigger()}

      {/* Expanded input - portal overlay */}
      {shouldRenderPortal &&
        createPortal(
          <AnimatePresence>
            {/* Backdrop */}
            <motion.div
              key='backdrop'
              className={`fixed inset-0 ${backdropZIndex} bg-black/20`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={handleCancel}
              aria-hidden='true'
            />

            {/* Mobile vs Desktop */}
            {isTablet ? renderMobileSheet() : renderDesktopCard()}
          </AnimatePresence>,
          document.body
        )}
    </>
  );
}
